import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Info } from 'lucide-react';

/**
 * TikTok-specific upload settings component
 * Implements all required UX elements per TikTok Content Sharing Guidelines
 * Order follows Points 1-5 of https://developers.tiktok.com/doc/content-sharing-guidelines
 */
function TikTokSettings({ 
  creatorInfo, 
  settings, 
  onSettingsChange,
  videoType = 'video' // 'video' or 'photo'
}) {
  const { t } = useTranslation();
  const [brandedContentWarning, setBrandedContentWarning] = useState('');
  const [brandedContentBlocked, setBrandedContentBlocked] = useState(false);

  // Handle privacy level change
  const handlePrivacyChange = (e) => {
    const newPrivacy = e.target.value;
    
    // If user selects private and branded content is enabled, block and warn
    if (newPrivacy === 'SELF_ONLY' && settings.brandContentToggle && settings.brandedContent) {
      setBrandedContentWarning(t('app.tiktok.warnings.privateNotAllowed'));
      return;
    }

    setBrandedContentWarning('');
    onSettingsChange({ ...settings, privacyLevel: newPrivacy });
  };

  // Handle commercial content toggle
  const handleCommercialToggle = (enabled) => {
    if (!enabled) {
      // When turning off, reset all brand settings and warnings
      setBrandedContentBlocked(false);
      setBrandedContentWarning('');
      onSettingsChange({
        ...settings,
        brandContentToggle: false,
        yourBrand: false,
        brandedContent: false
      });
    } else {
      onSettingsChange({
        ...settings,
        brandContentToggle: true
      });
    }
  };

  // Handle brand selection
  const handleBrandSelection = (type, checked) => {
    const newSettings = { ...settings, [type]: checked };

    // If selecting branded content and privacy is private, try to switch to first available public option
    if (type === 'brandedContent' && checked && settings.privacyLevel === 'SELF_ONLY') {
      const publicOption = (creatorInfo?.privacy_level_options || []).find(o => o !== 'SELF_ONLY');
      if (publicOption) {
        // A public/friends option exists — auto-switch and inform the user
        newSettings.privacyLevel = publicOption;
        setBrandedContentBlocked(false);
        const publicLabel = publicOption === 'PUBLIC_TO_EVERYONE' ? t('app.tiktok.privacy.publicFull')
          : publicOption === 'MUTUAL_FOLLOW_FRIENDS' ? t('app.tiktok.privacy.friends')
          : publicOption === 'FOLLOWER_OF_CREATOR' ? t('app.tiktok.privacy.followers') : publicOption;
        setBrandedContentWarning(t('app.tiktok.warnings.autoChanged', { publicLabel }));
      } else {
        // Sandbox / only SELF_ONLY available — block the selection entirely
        setBrandedContentBlocked(true);
        setBrandedContentWarning('');
        return; // do not apply the change
      }
    }

    // Clear warnings when user unchecks branded content
    if (type === 'brandedContent' && !checked) {
      setBrandedContentBlocked(false);
      setBrandedContentWarning('');
    }

    onSettingsChange(newSettings);
  };

  // Get brand content label text
  const getBrandContentLabel = () => {
    if (settings.yourBrand && settings.brandedContent) {
      return t('app.tiktok.commercial.paidPartnershipLabel');
    } else if (settings.brandedContent) {
      return t('app.tiktok.commercial.paidPartnershipLabel');
    } else if (settings.yourBrand) {
      return t('app.tiktok.commercial.promotionalLabel');
    }
    return '';
  };

  // Get consent text based on settings
  const getConsentText = () => {
    if (settings.brandedContent) {
      return (
        <>
          {t('app.upload.tiktokConsentBrandedPrefix')}{' '}
          <a 
            href="https://www.tiktok.com/legal/page/global/bc-policy/en" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Branded Content Policy
          </a>
          {' '}{t('app.upload.and')}{' '}
          <a 
            href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Music Usage Confirmation
          </a>
          {' '}{t('app.upload.tiktokTermsSuffix')}
        </>
      );
    } else {
      return (
        <>
          {t('app.upload.tiktokConsentPrefix')}{' '}
          <a 
            href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Music Usage Confirmation
          </a>
          {' '}{t('app.upload.tiktokTermsSuffix')}
        </>
      );
    }
  };

  // Check if creator is rate limited or restricted
  const isCreatorRestricted = () => {
    if (!creatorInfo) return { restricted: false, reason: null };
    
    // Check for various restriction scenarios
    if (creatorInfo.creator_avatar_url === null && creatorInfo.creator_nickname === null) {
      return { restricted: true, reason: t('app.tiktok.errors.accountDataUnavailable') };
    }
    
    // Check if privacy_level_options is empty or missing
    if (!creatorInfo.privacy_level_options || creatorInfo.privacy_level_options.length === 0) {
      return { restricted: true, reason: t('app.tiktok.errors.cannotPublishAccount') };
    }
    
    // Check for daily posting limit (if available in API response)
    if (creatorInfo.max_posts_per_day !== undefined && creatorInfo.posts_today !== undefined) {
      if (creatorInfo.posts_today >= creatorInfo.max_posts_per_day) {
        return { restricted: true, reason: t('app.tiktok.errors.dailyLimit', { maxPosts: creatorInfo.max_posts_per_day }) };
      }
    }
    
    return { restricted: false, reason: null };
  };

  if (!creatorInfo) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          <Info className="inline mr-2" size={16} />
          {t('app.tiktok.loadingCreatorInfo')}
        </p>
      </div>
    );
  }

  // Check if creator is restricted from posting
  const restrictionCheck = isCreatorRestricted();
  if (restrictionCheck.restricted) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">
          <AlertCircle className="inline mr-2" size={16} />
          {restrictionCheck.reason}
        </p>
      </div>
    );
  }

  // Check if creator can post (note: comment_disabled only affects the comment interaction, not posting ability)
  if (!creatorInfo.privacy_level_options || creatorInfo.privacy_level_options.length === 0) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">
          <AlertCircle className="inline mr-2" size={16} />
          {t('app.tiktok.errors.cannotPublishNow')}
        </p>
      </div>
    );
  }

  const privacyOptions = creatorInfo.privacy_level_options || [];
  const duetDisabled = creatorInfo.duet_disabled || false;
  const stitchDisabled = creatorInfo.stitch_disabled || false;
  const commentDisabled = creatorInfo.comment_disabled || false;

  return (
    <div className="space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      {/* Point 1a: Display creator nickname so users know which account content will be posted to */}
      <div className="flex items-center space-x-3 mb-2 pb-3 border-b border-gray-200">
        {creatorInfo.creator_avatar_url && (
          <img
            src={creatorInfo.creator_avatar_url}
            alt="TikTok avatar"
            className="w-9 h-9 rounded-full object-cover border border-gray-300"
          />
        )}
        <div>
          <h3 className="font-semibold text-gray-800">{t('app.tiktok.title')}</h3>
          {(creatorInfo.creator_nickname || creatorInfo.creator_username) && (
            <p className="text-xs text-gray-500">
              {t('app.tiktok.account')}: <span className="font-medium text-gray-700">{creatorInfo.creator_nickname || `@${creatorInfo.creator_username}`}</span>
            </p>
          )}
        </div>
      </div>

      {/* Privacy Level - REQUIRED, NO DEFAULT */}
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {t('app.tiktok.visibility')} *
        </label>
        <select
          value={settings.privacyLevel || ''}
          onChange={handlePrivacyChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none bg-white"
          required
        >
          <option value="" disabled>{t('app.tiktok.selectVisibility')}</option>
          {privacyOptions.map((option) => {
            const isDisabled = option === 'SELF_ONLY' && settings.brandContentToggle && settings.brandedContent;
            return (
              <option 
                key={option} 
                value={option}
                disabled={isDisabled}
                title={isDisabled ? t('app.tiktok.warnings.privateNotAllowed') : undefined}
              >
                {option === 'PUBLIC_TO_EVERYONE' && t('app.tiktok.privacy.publicFull')}
                {option === 'MUTUAL_FOLLOW_FRIENDS' && t('app.tiktok.privacy.friends')}
                {option === 'SELF_ONLY' && (isDisabled ? t('app.tiktok.privacy.privateBlocked') : t('app.tiktok.privacy.privateFull'))}
                {option === 'FOLLOWER_OF_CREATOR' && t('app.tiktok.privacy.followers')}
              </option>
            );
          })}
        </select>
        {settings.brandContentToggle && settings.brandedContent && (
          <p className="text-xs text-orange-600 mt-1 flex items-center font-medium">
            <AlertCircle size={12} className="mr-1" />
            {t('app.tiktok.warnings.privateNotAllowed')}
          </p>
        )}
        {brandedContentWarning && (
          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-xs text-yellow-800 flex items-center">
              <AlertCircle size={12} className="mr-1" />
              {brandedContentWarning}
            </p>
          </div>
        )}
        {brandedContentBlocked && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
            <p className="text-xs text-red-800 flex items-center">
              <AlertCircle size={12} className="mr-1" />
              {t('app.tiktok.warnings.paidPartnershipBlocked')}
            </p>
          </div>
        )}
      </div>

      {/* Interaction Settings - NO DEFAULTS, must be manually enabled */}
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {t('app.tiktok.interactionSettings')}
        </label>
        <div className="space-y-2">
          <label className={`flex items-center ${commentDisabled ? 'opacity-50' : ''}`}>
            <input
              type="checkbox"
              checked={!settings.disableComment}
              onChange={(e) => onSettingsChange({ ...settings, disableComment: !e.target.checked })}
              disabled={commentDisabled}
              className="mr-2 w-4 h-4"
            />
            <span className="text-sm">{t('app.tiktok.enableComments')}</span>
          </label>

          {videoType === 'video' && (
            <>
              <label className={`flex items-center ${duetDisabled ? 'opacity-50' : ''}`}>
                <input
                  type="checkbox"
                  checked={!settings.disableDuet}
                  onChange={(e) => onSettingsChange({ ...settings, disableDuet: !e.target.checked })}
                  disabled={duetDisabled}
                  className="mr-2 w-4 h-4"
                />
                  <span className="text-sm">{t('app.tiktok.enableDuet')}</span>
              </label>

              <label className={`flex items-center ${stitchDisabled ? 'opacity-50' : ''}`}>
                <input
                  type="checkbox"
                  checked={!settings.disableStitch}
                  onChange={(e) => onSettingsChange({ ...settings, disableStitch: !e.target.checked })}
                  disabled={stitchDisabled}
                  className="mr-2 w-4 h-4"
                />
                  <span className="text-sm">{t('app.tiktok.enableStitch')}</span>
              </label>
            </>
          )}
        </div>
      </div>

      {/* Commercial Content Disclosure - OFF by default */}
      <div className="mb-4 p-3 bg-white rounded border">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-700">
            {t('app.tiktok.commercialDisclosure')}
          </label>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.brandContentToggle}
              onChange={(e) => handleCommercialToggle(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {settings.brandContentToggle && (
          <div className="mt-3 space-y-2">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={settings.yourBrand}
                onChange={(e) => handleBrandSelection('yourBrand', e.target.checked)}
                className="mr-2 mt-1 w-4 h-4"
              />
              <div>
                <span className="text-sm font-medium">{t('app.tiktok.yourBrand')}</span>
                <p className="text-xs text-gray-600">{t('app.tiktok.yourBrandDescription')}</p>
              </div>
            </label>

            <label className="flex items-start">
              <input
                type="checkbox"
                checked={settings.brandedContent}
                onChange={(e) => handleBrandSelection('brandedContent', e.target.checked)}
                className="mr-2 mt-1 w-4 h-4"
              />
              <div>
                <span className="text-sm font-medium">{t('app.tiktok.paidPartnership')}</span>
                <p className="text-xs text-gray-600">{t('app.tiktok.paidPartnershipDescription')}</p>
              </div>
            </label>

            {getBrandContentLabel() && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                <p className="text-xs text-blue-800">{getBrandContentLabel()}</p>
              </div>
            )}

            {settings.brandContentToggle && !settings.yourBrand && !settings.brandedContent && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-xs text-yellow-800 flex items-center">
                  <AlertCircle size={12} className="mr-1" />
                  {t('app.tiktok.needContentType')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Point 4: Compliance Requirements - Required Consent Declaration */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded">
        <p className="text-xs text-gray-700">
          {getConsentText()}
        </p>
      </div>

      {/* Point 5d: Notify users about processing time */}
      <div className="p-3 bg-gray-50 border border-gray-200 rounded">
        <p className="text-xs text-gray-600 flex items-center">
          <Info size={12} className="mr-1 flex-shrink-0" />
          {t('app.tiktok.processingDelay')}
        </p>
      </div>
    </div>
  );
}

export default TikTokSettings;
